/**
 * The **one live event board** (Chassis Slice H, PRD #534 D7 / ADR-0271): the
 * shared server-anchored, client-ticked top-centre overlay behind the Rat Swarm
 * board (Slice F, PRD #222 / ADR-0121) and the Blackthorn's Storm board (Slice F,
 * PRD #289 / ADR-0153), which shipped as hand-copied twins in `src/eventBoard.ts`
 * and `src/stormBoard.ts` and are folded here byte-for-byte-in-behaviour.
 *
 * What the core owns (the part the twins duplicated):
 *
 * - **The `init → {update, sync, reset}` contract** the installers drive: adopt a
 *   fresh status push (`update`), re-render the countdown each render frame
 *   between pushes (`sync`), and hide/disarm on a fresh connection (`reset`).
 * - **The server-anchored, client-ticked countdown** (the HUD's honesty bound):
 *   each push carries `ticksRemaining` in {@link EVENT_TICK_MS} server steps; the
 *   board anchors a wall-clock deadline from it and ticks down between pushes,
 *   snapping to every fresh push - it smooths between server truths, it never
 *   invents time.
 * - **The reconnect guard** (the swarm board's PR #243 lesson): the board only
 *   hides on an `active:false` push, and the server only seeds a status at login
 *   while an event is running - so a reconnect that spans the event's end (or a
 *   mid-event server restart, which wipes the in-memory event) would otherwise
 *   strand the board on screen forever with the countdown pinned at 0:00. The
 *   installers call `reset()` from `onWelcome`; if the event is genuinely still
 *   running, the mid-event seed push arrives in the same login flush and repaints
 *   the board immediately.
 * - **The component-owned DOM**: each board builds its own markup (same element
 *   ids as the old `index.html` blocks, so the declared CSS and any external
 *   lookups still bind) and appends it to `document.body` - `index.html` stops
 *   accreting per-event blocks. Pre-existing markup with the root id is adopted
 *   as-is, so a fixture-mounted document keeps working.
 *
 * Everything is `createElement`/`textContent` only - tally names are
 * player-controlled, so the chat/leaderboard XSS discipline applies - and the
 * boards render from status pushes alone (no world reads, nothing on the wire).
 * Placement stays the top-centre band (chat owns bottom-left, the progression HUD
 * bottom-centre, the toolbar top-right); the two events are mutually exclusive
 * (ADR-0151), so two boards never co-occupy it. The overlays are
 * `pointer-events: none` (CSS) so clicks always land on the canvas beneath.
 */
import { formatCountdown } from '../hud';
import type { RatSwarmStatusMessage, StormStatusMessage } from '../net/protocol';

/**
 * The length of one server tick in ms - what `ticksRemaining` counts. Must match
 * the server's `STEP_MS` (server/gameServer.ts), which the protocol pins at 50ms;
 * duplicated here (rather than imported) so the client bundle never pulls the
 * server module. Pinned by eventBoard.test.ts so a change is a conscious edit.
 */
export const EVENT_TICK_MS = 50;

/**
 * How many tally rows the swarm board shows: the podium, not the roster. The wire
 * carries every non-zero scorer (up to fifty), but the overlay must stay a
 * glanceable strip over the playfield, so it shows the race that matters - the
 * top of the winner-ordered list the server already sorted.
 */
export const EVENT_BOARD_MAX_ROWS = 5;

/** The shape every event-status push shares (the part the core reads). */
export interface EventBoardStatus {
  active: boolean;
  /** Time left on the event clock, in {@link EVENT_TICK_MS} server steps. */
  ticksRemaining: number;
}

/** Options for an event-board factory; injected by tests, defaulted live. */
export interface EventBoardOptions {
  /** The document to wire (defaults to the live `document`). */
  doc?: Document;
  /** Wall-clock source for the countdown anchor, defaulting to `performance.now()`. */
  now?: () => number;
}

/** The handle the installers drive an event board through. */
export interface EventBoardHandle<Msg extends EventBoardStatus> {
  /**
   * Adopt a fresh server status push: show the board on `active: true` (render
   * the body and re-anchor the countdown deadline), hide it on `active: false`
   * (the end push - the normal view returns).
   */
  update(msg: Msg): void;
  /**
   * Re-render the countdown from the anchored deadline at the current instant.
   * Called each render frame; a no-op while the board is hidden (or the
   * instance's body reported no running clock, e.g. the storm entrance).
   */
  sync(): void;
  /** Hide the board and disarm the countdown - the PR #243 reconnect guard. */
  reset(): void;
}

/** What a specific board (swarm/storm) contributes on top of the shared core. */
export interface EventBoardSpec<Msg extends EventBoardStatus> {
  /** The root element id (`#event-board` / `#storm-board`) - CSS binds to it. */
  rootId: string;
  /** The countdown span's id inside the root. */
  countdownId: string;
  /**
   * Build the board's markup (the old `index.html` block, same ids) when the
   * document doesn't already carry a `#rootId`. Must return the root, `hidden`.
   */
  build(doc: Document): HTMLElement;
  /**
   * Render everything except the countdown from an **active** push. Returns
   * whether the countdown is running (the storm's pre-summon entrance shows the
   * approach banner instead and returns false; the swarm always returns true).
   */
  renderBody(msg: Msg, doc: Document): boolean;
}

/** Adopt the existing `#rootId` markup or build + append the component's own. */
function ensureDom(doc: Document, spec: EventBoardSpec<never>): HTMLElement | null {
  const existing = doc.getElementById(spec.rootId);
  if (existing) return existing;
  if (!doc.body) return null;
  const root = spec.build(doc);
  doc.body.appendChild(root);
  return root;
}

/**
 * The shared core: DOM ownership, the active latch, the server-anchored deadline,
 * and the `update`/`sync`/`reset` contract. A missing root (a body-less document)
 * degrades to a harmless no-op handle - the HUD's own resilience rule.
 */
export function createEventBoard<Msg extends EventBoardStatus>(
  spec: EventBoardSpec<Msg>,
  opts: EventBoardOptions = {},
): EventBoardHandle<Msg> {
  const doc = opts.doc ?? document;
  const now = opts.now ?? (() => performance.now());
  const root = ensureDom(doc, spec as EventBoardSpec<never>);
  const countdown = doc.getElementById(spec.countdownId);

  /** The wall-clock instant the event times out, per the latest push. */
  let deadline = 0;
  let active = false;
  /** Whether the latest push left a clock running (false in the storm entrance). */
  let counting = false;

  const renderCountdown = (at: number): void => {
    if (countdown) countdown.textContent = formatCountdown(Math.max(0, deadline - at));
  };

  return {
    update(msg) {
      active = msg.active;
      if (!msg.active) {
        // The end push: the board disappears and the normal view returns. State
        // is left as-is - the next event's first push repaints everything.
        if (root) root.hidden = true;
        return;
      }
      const at = now();
      deadline = at + msg.ticksRemaining * EVENT_TICK_MS;
      counting = spec.renderBody(msg, doc);
      if (counting) renderCountdown(at);
      if (root) root.hidden = false;
    },
    sync() {
      if (!active || !counting) return;
      renderCountdown(now());
    },
    reset() {
      // Fresh-connection state: whatever is on screen belongs to the previous
      // socket's world. Stale rows/countdown are left in the hidden DOM - the
      // next active push (the login seed, if an event still runs) repaints all
      // of them before revealing.
      active = false;
      counting = false;
      if (root) root.hidden = true;
    },
  };
}

// ---------------------------------------------------------------------------
// The Rat Swarm board (Slice F, PRD #222 / ADR-0121)
// ---------------------------------------------------------------------------

/** Build the `#event-board` markup (the old `index.html` block, same ids). */
function buildSwarmBoard(doc: Document): HTMLElement {
  const root = doc.createElement('div');
  root.id = 'event-board';
  root.setAttribute('aria-label', 'Rat Swarm');
  root.hidden = true;
  const h2 = doc.createElement('h2');
  h2.textContent = 'Rat Swarm!';
  const meta = doc.createElement('div');
  meta.id = 'event-board-meta';
  // The meta spans carry no placeholder copy: every one is repainted from the
  // first active push before the (hidden-by-default) board is ever revealed.
  const rats = doc.createElement('span');
  rats.id = 'event-board-rats';
  const countdown = doc.createElement('span');
  countdown.id = 'event-board-countdown';
  meta.append(rats, countdown);
  const tallies = doc.createElement('ol');
  tallies.id = 'event-board-tallies';
  root.append(h2, meta, tallies);
  return root;
}

/**
 * Reconcile the tally rows in place (the leaderboard window's idiom): reuse
 * existing `<li>`s, rewrite only changed text, drop the surplus tail - so a
 * per-kill refresh never flickers. Rows are `name + kills` spans; the wire
 * order is already the winner order, so the board just takes the top of it.
 */
function renderTallies(doc: Document, entries: RatSwarmStatusMessage['tallies']): void {
  const tallies = doc.getElementById('event-board-tallies');
  if (!tallies) return;
  const shown = entries.slice(0, EVENT_BOARD_MAX_ROWS);
  for (const [i, entry] of shown.entries()) {
    let li = tallies.children[i] as HTMLLIElement | undefined;
    if (!li) {
      li = doc.createElement('li');
      const name = doc.createElement('span');
      name.className = 'eb-name';
      const kills = doc.createElement('span');
      kills.className = 'eb-kills';
      li.append(name, kills);
      tallies.appendChild(li);
    }
    const name = li.querySelector('.eb-name')!;
    const kills = li.querySelector('.eb-kills')!;
    // textContent (never innerHTML): tally names are player-controlled.
    if (name.textContent !== entry.name) name.textContent = entry.name;
    const killsText = String(entry.kills);
    if (kills.textContent !== killsText) kills.textContent = killsText;
  }
  while (tallies.childElementCount > shown.length) {
    tallies.removeChild(tallies.lastElementChild!);
  }
}

/**
 * The live **Rat Swarm event board**: top stompers (winner order, anonymous
 * session names included), the rats remaining, and the live countdown - all from
 * the server's {@link RatSwarmStatusMessage} pushes.
 */
export function initSwarmBoard(
  opts: EventBoardOptions = {},
): EventBoardHandle<RatSwarmStatusMessage> {
  return createEventBoard<RatSwarmStatusMessage>(
    {
      rootId: 'event-board',
      countdownId: 'event-board-countdown',
      build: buildSwarmBoard,
      renderBody(msg, doc) {
        const rats = doc.getElementById('event-board-rats');
        if (rats) rats.textContent = `${msg.ratsRemaining} rats left`;
        renderTallies(doc, msg.tallies);
        return true; // the swarm clock always runs while active
      },
    },
    opts,
  );
}

// ---------------------------------------------------------------------------
// The Blackthorn's Storm board (Slice F, PRD #289 / ADR-0153)
// ---------------------------------------------------------------------------

/** Build the `#storm-board` markup (the old `index.html` block, same ids). */
function buildStormBoard(doc: Document): HTMLElement {
  const root = doc.createElement('div');
  root.id = 'storm-board';
  root.setAttribute('aria-label', "Blackthorn's Storm");
  root.hidden = true;
  const h2 = doc.createElement('h2');
  h2.textContent = "Blackthorn's Storm!";
  const approach = doc.createElement('p');
  approach.id = 'storm-board-approach';
  approach.textContent = 'Blackthorn approaches…';
  approach.hidden = true;
  const meta = doc.createElement('div');
  meta.id = 'storm-board-meta';
  // No placeholder copy in the meta spans: both are repainted from the first
  // active push before the (hidden-by-default) board is ever revealed.
  const tally = doc.createElement('span');
  tally.id = 'storm-board-tally';
  const countdown = doc.createElement('span');
  countdown.id = 'storm-board-countdown';
  meta.append(tally, countdown);
  root.append(h2, approach, meta);
  return root;
}

/**
 * The live **Blackthorn's Storm event board**: a "Blackthorn approaches…" banner
 * during the pre-summon entrance (`vortexCount === 0`, the load-bearing entrance
 * signal - the clock is pinned at full with nothing to count down), then a
 * living-vs-fallen tally plus the combat-clock countdown - all from the server's
 * {@link StormStatusMessage} pushes. (The per-name death feed was dropped as
 * clutter, ADR-0156.)
 */
export function initStormBoard(
  opts: EventBoardOptions = {},
): EventBoardHandle<StormStatusMessage> {
  return createEventBoard<StormStatusMessage>(
    {
      rootId: 'storm-board',
      countdownId: 'storm-board-countdown',
      build: buildStormBoard,
      renderBody(msg, doc) {
        // No vortex summoned yet => Blackthorn is still descending (the 10s
        // entrance): the combat clock is pinned at the full length and hasn't
        // started, so there is nothing to count down. Show the approach banner.
        const approaching = msg.vortexCount === 0;
        const tally = doc.getElementById('storm-board-tally');
        if (tally) tally.textContent = `${msg.living} standing · ${msg.fallen} fallen`;
        const approach = doc.getElementById('storm-board-approach');
        if (approach) approach.hidden = !approaching;
        const meta = doc.getElementById('storm-board-meta');
        if (meta) meta.hidden = approaching;
        return !approaching;
      },
    },
    opts,
  );
}
